/**
 * Offline-tolerant physical-count line entry (WP7.8b, DECISIONS.md ADR-085 Amendment).
 *
 * Only *count lines* (a field observation the server still validates — session must be FROZEN, line
 * must exist, quantity a non-negative decimal) can be queued. Freeze/submit/approve are never queued:
 * they change the session's state / post the stock adjustment and must be online, server-decided
 * actions (AGENTS.md rules 4, 11).
 *
 * Decision table for "Save counts":
 * - browser tab (not the desktop shell): unchanged — a failure is shown, nothing is queued;
 * - desktop + online + server answers (2xx or a problem): the normal path, problems are surfaced;
 * - desktop + (`navigator.onLine === false` OR the request never got an HTTP answer): queued in the
 *   local outbox, replayed later in order with a client UUIDv7 idempotency key.
 *
 * Replaying a count batch that the server may already have applied (response lost after the write) is
 * harmless: recording a counted quantity sets the same value again, and the replay's own key makes the
 * server return the stored result. The only path that could re-send identical values is the
 * lost-response case just described; it cannot double-count because counts are *set*, not added.
 */
import { isDesktop } from './desktop.js';
import { enqueueOfflineCommand, type OutboxCommand } from './desktop-bridge.js';

export const COUNT_COMMAND_TYPE = 'inventory.count.recordCounts';

export interface RecordCountsBody {
  counts: { lineId: string; countedQuantity: string }[];
}

/** The full path (with the `/api/v1` prefix) the outbox replays against — the existing REST endpoint. */
export function countEndpoint(sessionId: string): string {
  return `/api/v1/inventory/counts/${sessionId}/counts`;
}

/** True when the failure is "no HTTP answer at all" (offline, DNS, refused, aborted), as opposed to a
 * problem response. `fetch` rejects with a `TypeError` for these; a thrown RFC 9457 problem object is
 * an answered request and must NOT be queued. */
export function isNetworkFailure(error: unknown): boolean {
  if (error instanceof TypeError) return true;
  if (error instanceof DOMException && (error.name === 'NetworkError' || error.name === 'AbortError')) return true;
  return false;
}

export type SubmitCountsResult<T> = { kind: 'submitted'; value: T } | { kind: 'queued'; command: OutboxCommand };

export interface SubmitCountsDeps<T> {
  sessionId: string;
  body: RecordCountsBody;
  /** The online call; resolves with the server result or rejects with a problem/network error. */
  send: () => Promise<T>;
  desktop?: boolean;
  online?: boolean;
  enqueue?: typeof enqueueOfflineCommand;
}

/** Sends the count lines, falling back to the offline outbox only when there is no HTTP answer. */
export async function submitOrQueueCounts<T>(deps: SubmitCountsDeps<T>): Promise<SubmitCountsResult<T>> {
  const desktop = deps.desktop ?? isDesktop();
  const online = deps.online ?? (typeof navigator === 'undefined' ? true : navigator.onLine);
  const enqueue = deps.enqueue ?? enqueueOfflineCommand;

  const queue = async (): Promise<SubmitCountsResult<T>> => ({
    kind: 'queued',
    command: await enqueue(COUNT_COMMAND_TYPE, 'POST', countEndpoint(deps.sessionId), deps.body),
  });

  if (desktop && !online) return queue();
  try {
    return { kind: 'submitted', value: await deps.send() };
  } catch (error) {
    if (desktop && isNetworkFailure(error)) return queue();
    throw error;
  }
}

/** The count session id a queued count command targets, parsed from its endpoint; `null` for any other
 * command. Used to show "N lines queued" on the matching session page. */
export function sessionIdOfCountCommand(command: Pick<OutboxCommand, 'commandType' | 'endpoint'>): string | null {
  if (command.commandType !== COUNT_COMMAND_TYPE) return null;
  const match = /\/inventory\/counts\/([^/]+)\/counts$/.exec(command.endpoint);
  return match?.[1] ?? null;
}

/** Number of count lines carried by a queued count command (0 when the payload is unreadable). */
export function countLinesInCommand(command: Pick<OutboxCommand, 'payloadJson'>): number {
  try {
    const parsed = JSON.parse(command.payloadJson) as Partial<RecordCountsBody> | null;
    return Array.isArray(parsed?.counts) ? parsed.counts.length : 0;
  } catch {
    return 0;
  }
}

/** Queued (not yet completed, not failed) count lines for one session — the "offline pending" badge. */
export function pendingCountLines(commands: readonly OutboxCommand[], sessionId: string): number {
  return commands
    .filter((c) => (c.status === 'PENDING' || c.status === 'SENDING') && sessionIdOfCountCommand(c) === sessionId)
    .reduce((sum, c) => sum + countLinesInCommand(c), 0);
}
